import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppStore } from '../../stores/appStore';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { AlertCircle, Plus, Building2, Edit2 } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';

export function OrganizationList() {
  const { organizations, loading, error, fetchOrganizations, clearError } = useAppStore();
  const { isSuperAdmin } = useAuth();

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    clearError();
    fetchOrganizations().catch((error) => {
      // Error is already handled in the store and displayed via error state
      console.error('Failed to fetch organizations:', error);
    });
  }, [fetchOrganizations, clearError]);

  if (!isSuperAdmin()) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            Only super administrators can manage organizations.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Organizations</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Manage multi-tenant organizations</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/dashboard/orgs/create">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Organization
            </Button>
          </Link>
          <ThemeToggleCompact />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size={48} />
        </div>
      ) : organizations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Building2 className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Organizations Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first organization to get started
            </p>
            <Link to="/dashboard/orgs/create">
              <Button>Create Organization</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {organizations.map((org) => (
            <Card key={org.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">{org.name}</CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/dashboard/orgs/${org.id}`}>
                      <Button variant="outline" size="sm">
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">ID:</span>
                    <span className="ml-2 font-mono text-xs">{org.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Domain:</span>
                    <span className="ml-2 font-medium">
                      {org.domain || (
                        <span className="text-gray-400 dark:text-gray-500 italic">Not set</span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Created:</span>
                    <span className="ml-2">{formatDate(org.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Updated:</span>
                    <span className="ml-2">{formatDate(org.updated_at)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
